import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../../components/ui/Icon';
import { PButton } from './ui';
import { useTenantAccess } from './context';

/**
 * Cross-tenant access is a deliberate, logged action (Architecture §5.3 /
 * UX §3) — while a tenant is open this banner keeps that visible, names the
 * case reference every request is being logged under, and offers a one-click
 * exit. Ochre-on-navy (`accent` on `primary`) is the ONE color pairing the
 * design system carries across both the light Tenant Shell and the dark
 * Platform Console unchanged (DESIGN.md `surface-depth.platform-banner`) —
 * every reference screen (13–18) renders this banner identically regardless
 * of which dark screen it sits on, which is what Phase 5's exit criteria
 * checks directly.
 */
export function TenantAccessBanner() {
  const { access, clear } = useTenantAccess();
  const navigate = useNavigate();
  const location = useLocation();

  if (!access) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-accent bg-accent px-4 py-2.5 text-sm text-primary shadow-card sm:px-6">
      <div className="flex items-center gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-tight bg-primary text-white">
          <Icon name="warning" className="text-[17px]" />
        </div>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium">
          <span className="rounded-tight border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider">
            Tenant workspace open
          </span>
          <span>
            Tenant <code className="rounded bg-white/40 px-1 py-0.5 font-mono text-xs">{access.tenantId}</code>
          </span>
          <span className="hidden text-primary/50 md:inline">·</span>
          <span>
            Case <span className="font-semibold">{access.caseReference}</span> — every request is audited.
          </span>
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!location.pathname.startsWith('/platform/tenant') && (
          <PButton variant="secondary" className="h-7 bg-primary text-xs text-white hover:bg-primary-hover" onClick={() => navigate('/platform/tenant')}>
            Open workspace
          </PButton>
        )}
        <PButton
          variant="secondary"
          className="h-7 border-primary/30 bg-transparent text-xs text-primary hover:bg-primary/10"
          onClick={() => {
            clear();
            navigate('/platform/lookup');
          }}
        >
          Exit tenant
        </PButton>
      </div>
    </div>
  );
}
